import type { ResourceMembership } from 'v5/core/entity-schema/entitySchema.types';

/** One `contacts ↔ enterprise_workspaces` join row. `null` = global membership. */
export type ResourceMembershipRow = { enterprise_workspace_id: number | null };

/**
 * `undefined` / `[]` → hidden; any `null` row → global; otherwise the distinct
 * workspace ids in row order.
 */
export function membershipFromRows(
  rows: ReadonlyArray<ResourceMembershipRow> | undefined
): ResourceMembership {
  if (rows === undefined || rows.length === 0) {
    return { type: 'hidden' };
  }
  if (rows.some(row => row.enterprise_workspace_id === null)) {
    return { type: 'global' };
  }
  const workspaceIds: number[] = [];
  for (const row of rows) {
    const id = row.enterprise_workspace_id;
    if (id !== null && !workspaceIds.includes(id)) {
      workspaceIds.push(id);
    }
  }
  return { type: 'workspaces', workspaceIds };
}

/**
 * Workspaces the resource is a member of, restricted to (and ordered by)
 * `allWorkspaceIds`. `global` → all of them; `hidden` → none; explicit
 * membership → the listed ids that are also in `allWorkspaceIds`.
 */
export function memberWorkspaceIds(
  membership: ResourceMembership,
  allWorkspaceIds: ReadonlyArray<number>
): number[] {
  switch (membership.type) {
    case 'hidden':
      return [];
    case 'global':
      return [...allWorkspaceIds];
    case 'workspaces': {
      const members = new Set(membership.workspaceIds);
      return allWorkspaceIds.filter(id => members.has(id));
    }
  }
}
